import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";
import { OpenAPI } from "@/service";
import { i18n } from "./plugins/i18n";
import "./plugins/vee-validate";
import "./plugins/confirm";
import "./plugins/alert";
import "./plugins/clipboard";
import "./plugins/showdown";
import "./plugins/infinite-loading";
import "./plugins/eventbus";
import "./plugins/prismic";
import "./plugins/openreplay";
import "./plugins/globals";
import "./plugins/dirtycheck";
import "./plugins/matomo";
import "./plugins/lightBox";

import { RequestHookParams } from "./service/core/OpenAPI";


Vue.config.productionTip = false;

OpenAPI.BASE = process.env.VUE_APP_API_BASE_URI as string;

if (process.env.NODE_ENV === "development") {
  OpenAPI.REQUEST_HOOK = async ({ url, options }: RequestHookParams) => {
    return {
      url: /\?/.test(url) ? `${url}&XDEBUG_TRIGGER=PHPSTORM` : `${url}?XDEBUG_TRIGGER=PHPSTORM`,
      options
    };
  };
}
let ignoreNextSessionTermination = false;

export function markImpersonationSwitch() {
  ignoreNextSessionTermination = true;
}

function shouldIgnoreSessionTermination() {
  if (ignoreNextSessionTermination) {
    ignoreNextSessionTermination = false; // consume once
    return true;
  }
  return false;
}

OpenAPI.RESPONSE_HOOK = async r => {
  // Could use headers.get('X-Cen-Status') for logged out status or headers.get('X-Cen-Token-Update') to replace the token
  const result = r.result;
  // const status = (r.response as Response).headers.get("X-Cen-Status");
  const status = r.response?.headers.get("X-Cen-Status");
  if (status == "session-terminated") {
     // THIS FIX IS DONE TO AVOID LOGOUT ON RETURN TO CSR
    if (shouldIgnoreSessionTermination()) {
      return result; // do NOT logout, this is from cancelLoginAs
    }

    console.log("Session terminated!");
    await store.dispatch("cacheUserId");
    await store.dispatch("resetCredentials");
    await store.dispatch("initialize");
    store.commit("setSnackbar", i18n.t("auth.loggedOut") as string);

    // Massage result as this error is handled
    return {
      url: result.url,
      ok: true,
      status: 200,
      statusText: "OK",
      body: null
    };
  }
  const update = (r.response as Response).headers.get("X-Cen-Token-Update");
  if (update) {
    store.dispatch("updateToken", { token: update });
  }
  const version = (r.response as Response).headers.get("X-Cen-Server-Version");

  if (version) {
    store.commit("setServerVersion", version);
  }
  return result;
};

function isStorageAvailable(): boolean {
  try {
    const testKey = "__storage_test__";
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

if (!isStorageAvailable()) {
  const appEl = document.getElementById("app");
  if (appEl) {
    appEl.innerHTML = `
      <div class="storage-blocked-container">
        <div class="storage-blocked-box">
          <div class="storage-blocked-icon">&#9888;</div>
          <h2>Cookies & Storage Required</h2>
          <p>
            This application requires browser cookies and local storage to work correctly.
            It looks like your browser is blocking storage access.
          </p>
          <p>To fix this, please:</p>
          <ul>
            <li>Enable cookies and site data in your browser settings</li>
            <li>Or allow cookies for this site specifically</li>
          </ul>
        </div>
      </div>
      <style>
        .storage-blocked-container {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100vh;
          background: #f5f5f5;
          font-family: sans-serif;
        }
        .storage-blocked-box {
          text-align: center;
          max-width: 480px;
          padding: 40px;
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 2px 16px rgba(0,0,0,0.12);
        }
        .storage-blocked-icon {
          font-size: 48px;
          color: #e65100;
          margin-bottom: 16px;
        }
        .storage-blocked-box h2 {
          color: #b71c1c;
          margin-bottom: 12px;
        }
        .storage-blocked-box p, .storage-blocked-box ul {
          color: #555;
          text-align: left;
          line-height: 1.6;
        }
        .storage-blocked-box button {
          margin-top: 24px;
          padding: 10px 28px;
          background: #1565c0;
          color: #fff;
          border: none;
          border-radius: 4px;
          font-size: 15px;
          cursor: pointer;
        }
      </style>
    `;
  }
} else {
  store.dispatch("initialize").then(() => {
    new Vue({
      router,
      store,
      vuetify,
      i18n,
      render: h => h(App),
      // Initailize store on creation
      beforeCreate(): void {
        // Not sure why the this fails type inspection
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        //(this.$store as any).commit("initStore");
        //store.dispatch("initialize");
        // store.commit("initStore");
      }
    }).$mount("#app");
  });
}
